import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowLeft,
  ArrowUp,
  ChevronRight,
  ExternalLink,
  Home,
  Maximize2,
  MessageSquareText,
  Minimize2,
  Send,
} from 'lucide-react'
import { useAuth } from '@/components/auth/AuthContext'
import { ChromeIcon } from '@/components/ChromeIcon'
import { ClayCountBadge, ClayDot } from '@/components/ClayPunctuation'
import { useModalA11y } from '@/hooks/useModalA11y'
import { formatRelative } from '@/lib/utils'
import {
  useAddHelpMessage,
  useCreateHelpRequest,
  useHelpRequest,
  useHelpRequests,
  useUnreadHelpCount,
} from '@/modules/help'
import { WEB_ASSETS, HELP_TOPICS } from '@tenfour/contracts'
import { STATUS_PAGE_URL } from './articleIndex'
import styles from './help.module.css'

type View =
  | { name: 'home' }
  | { name: 'list' }
  | { name: 'new' }
  | { name: 'thread'; id: string }

interface SupportMessengerProps {
  onClose: () => void
  /** Land straight on a thread (reply-toast click). */
  initialThreadId: string | null
  /** Lets the surface suppress the toast for the thread on screen. */
  onThreadViewed: (threadId: string | null) => void
}

function TenFourAvatar({ size = 32 }: { size?: number }) {
  return (
    <span
      className={styles.brandAvatar}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <img src={WEB_ASSETS.brand.favicon} alt="" />
    </span>
  )
}

/**
 * The floating Intercom-style support messenger (reference:
 * references/helpcenter/apps-web/support-*.png + helpcenter.mp4). Home and
 * Messages tabs over the existing /help-requests backend; a new conversation
 * opens with topic chips that become the first message (and the thread's
 * `topic` tag, which /admin/helpdesk triages by).
 */
export function SupportMessenger({ onClose, initialThreadId, onThreadViewed }: SupportMessengerProps) {
  const { user } = useAuth()
  const [view, setView] = useState<View>(
    initialThreadId ? { name: 'thread', id: initialThreadId } : { name: 'home' },
  )
  // Direction steers the push/pop slide animation on view changes.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [expanded, setExpanded] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [pendingChip, setPendingChip] = useState<string | null>(null)
  const dialogRef = useModalA11y<HTMLDivElement>(true, onClose)

  const listQuery = useHelpRequests('account')
  const { unread } = useUnreadHelpCount('account')
  const activeThreadId = view.name === 'thread' ? view.id : null
  const detailQuery = useHelpRequest('account', activeThreadId)
  const createMutation = useCreateHelpRequest('account')
  const addMessageMutation = useAddHelpMessage('account', activeThreadId ?? '')

  const requests = listQuery.data ?? []
  const recent = requests[0] ?? null

  useEffect(() => {
    onThreadViewed(activeThreadId)
    return () => onThreadViewed(null)
  }, [activeThreadId, onThreadViewed])

  const go = useCallback((next: View, dir: 'forward' | 'back') => {
    setDirection(dir)
    setView(next)
  }, [])

  // Auto-scroll the thread to the newest message.
  const threadEndRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [view.name, detailQuery.data?.messages.length, pendingChip])

  const startFromChip = async (topic: string) => {
    if (createMutation.isPending) return
    setPendingChip(topic)
    try {
      const created = await createMutation.mutateAsync({ subject: topic, body: topic, topic })
      setPendingChip(null)
      go({ name: 'thread', id: created.id }, 'forward')
    } catch {
      setPendingChip(null)
    }
  }

  const startFromText = async () => {
    const body = replyText.trim()
    if (!body || createMutation.isPending) return
    setPendingChip(body)
    setReplyText('')
    try {
      const created = await createMutation.mutateAsync({
        subject: body.length > 60 ? `${body.slice(0, 57)}…` : body,
        body,
      })
      setPendingChip(null)
      go({ name: 'thread', id: created.id }, 'forward')
    } catch {
      setPendingChip(null)
      setReplyText(body)
    }
  }

  const sendReply = async () => {
    const body = replyText.trim()
    if (!body || !activeThreadId || addMessageMutation.isPending) return
    setReplyText('')
    try {
      await addMessageMutation.mutateAsync(body)
    } catch {
      setReplyText(body)
    }
  }

  const composerKey = (e: React.KeyboardEvent<HTMLTextAreaElement>, submit: () => void) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const firstName = user?.name?.split(/\s+/)[0] ?? 'there'
  const detail = detailQuery.data ?? null

  const viewKey = view.name === 'thread' ? `thread-${view.id}` : view.name
  const paneClass = [
    styles.messengerPane,
    direction === 'forward' ? styles.paneForward : styles.paneBack,
  ].join(' ')

  return createPortal(
    <div className={styles.messengerRoot}>
      <button
        type="button"
        className={styles.drawerScrim}
        aria-label="Close support"
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        className={[styles.messenger, expanded && styles.messengerExpanded]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Ten Four support"
        tabIndex={-1}
      >
        {/* ── Home ─────────────────────────────────────────────────────── */}
        {view.name === 'home' && (
          <div key={viewKey} className={[paneClass, styles.homePane].join(' ')}>
            <div className={styles.homeHero}>
              <div className={styles.homeHeroTop}>
                <TenFourAvatar size={34} />
                <button type="button" className={styles.iconBtnLight} aria-label="Close support" onClick={onClose}>
                  <ChromeIcon name="close" size={18} />
                </button>
              </div>
              <h2 className={styles.homeGreeting}>
                Hi {firstName} <span aria-hidden>👋</span>
                <br />
                How can we help?
              </h2>
            </div>
            <div className={styles.homeCards}>
              {recent && (
                <button
                  type="button"
                  className={styles.homeCard}
                  onClick={() => go({ name: 'thread', id: recent.id }, 'forward')}
                >
                  <span className={styles.homeCardLabel}>Recent message</span>
                  <span className={styles.recentRow}>
                    <TenFourAvatar size={30} />
                    <span className={styles.recentText}>
                      <span className={styles.recentName}>Ten Four Support</span>
                      <span className={styles.recentPreview}>
                        {recent.lastMessagePreview ?? recent.subject}
                      </span>
                    </span>
                    <span className={styles.recentMeta}>
                      {recent.lastMessageAt ? formatRelative(recent.lastMessageAt) : ''}
                      {recent.unreadCount > 0 && <ClayDot label="Unread" className={styles.unreadDot} />}
                    </span>
                  </span>
                </button>
              )}
              <button
                type="button"
                className={styles.homeCard}
                onClick={() => go({ name: 'new' }, 'forward')}
              >
                <span className={styles.homeCardRow}>
                  <span className={styles.homeCardText}>
                    <span className={styles.homeCardTitle}>Send us a message</span>
                    <span className={styles.homeCardSub}>We typically reply in a few hours</span>
                  </span>
                  <Send size={16} aria-hidden className={styles.homeCardGlyph} />
                </span>
              </button>
              <a className={styles.homeCard} href={STATUS_PAGE_URL} target="_blank" rel="noreferrer">
                <span className={styles.homeCardRow}>
                  <span className={styles.homeCardTitle}>Ten Four status</span>
                  <ExternalLink size={15} aria-hidden className={styles.homeCardGlyph} />
                </span>
              </a>
            </div>
          </div>
        )}

        {/* ── Messages list ────────────────────────────────────────────── */}
        {view.name === 'list' && (
          <div key={viewKey} className={paneClass}>
            <header className={styles.messengerHeader}>
              <span className={styles.messengerHeaderTitle}>Messages</span>
              <button type="button" className={styles.iconBtn} aria-label="Close support" onClick={onClose}>
                <ChromeIcon name="close" size={18} />
              </button>
            </header>
            <div className={styles.listBody}>
              {requests.length === 0 && !listQuery.isLoading ? (
                <div className={styles.listEmpty}>
                  <MessageSquareText size={26} aria-hidden />
                  <span>No messages yet — say hello and the team will reply here.</span>
                </div>
              ) : (
                requests.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className={styles.listRow}
                    onClick={() => go({ name: 'thread', id: r.id }, 'forward')}
                  >
                    <TenFourAvatar size={34} />
                    <span className={styles.recentText}>
                      <span className={styles.recentName}>Ten Four Support</span>
                      <span className={styles.recentPreview}>
                        {r.lastMessagePreview ?? r.subject}
                      </span>
                    </span>
                    <span className={styles.recentMeta}>
                      {r.lastMessageAt ? formatRelative(r.lastMessageAt) : formatRelative(r.createdAt)}
                      {r.unreadCount > 0 && <ClayDot label="Unread" className={styles.unreadDot} />}
                    </span>
                  </button>
                ))
              )}
            </div>
            <div className={styles.listCtaWrap}>
              <button
                type="button"
                className={styles.listCta}
                onClick={() => go({ name: 'new' }, 'forward')}
              >
                Send us a message <Send size={15} aria-hidden />
              </button>
            </div>
          </div>
        )}

        {/* ── New conversation (welcome + topic chips) ─────────────────── */}
        {view.name === 'new' && (
          <div key={viewKey} className={paneClass}>
            <header className={styles.messengerHeader}>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Back"
                onClick={() => go({ name: 'home' }, 'back')}
              >
                <ArrowLeft size={18} />
              </button>
              <span className={styles.threadIdentity}>
                <TenFourAvatar size={30} />
                <span className={styles.threadIdentityText}>
                  <span className={styles.recentName}>Ten Four Support</span>
                  <span className={styles.threadSub}>We typically reply in a few hours</span>
                </span>
              </span>
              <button type="button" className={styles.iconBtn} aria-label="Close support" onClick={onClose}>
                <ChromeIcon name="close" size={18} />
              </button>
            </header>
            <div className={styles.threadBody}>
              <div className={styles.bubbleIn}>Welcome! What brings you here today?</div>
              {pendingChip ? (
                <div className={styles.bubbleOut}>{pendingChip}</div>
              ) : (
                <div className={styles.chipCloud} role="group" aria-label="Pick a topic">
                  {HELP_TOPICS.map((topic) => (
                    <button
                      key={topic}
                      type="button"
                      className={styles.chip}
                      disabled={createMutation.isPending}
                      onClick={() => startFromChip(topic)}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              )}
              <div ref={threadEndRef} />
            </div>
            <div className={styles.composerWrap}>
              <div className={styles.composer}>
                <textarea
                  className={styles.composerInput}
                  placeholder="Message…"
                  rows={1}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => composerKey(e, startFromText)}
                  aria-label="Message"
                />
                <button
                  type="button"
                  className={styles.composerSend}
                  aria-label="Send message"
                  disabled={createMutation.isPending || replyText.trim().length === 0}
                  onClick={startFromText}
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Thread ───────────────────────────────────────────────────── */}
        {view.name === 'thread' && (
          <div key={viewKey} className={paneClass}>
            <header className={styles.messengerHeader}>
              <button
                type="button"
                className={styles.iconBtn}
                aria-label="Back to messages"
                onClick={() => go({ name: 'list' }, 'back')}
              >
                <ArrowLeft size={18} />
              </button>
              <span className={styles.threadIdentity}>
                <TenFourAvatar size={30} />
                <span className={styles.threadIdentityText}>
                  <span className={styles.recentName}>Ten Four Support</span>
                  <span className={styles.threadSub}>The team typically replies in a few hours</span>
                </span>
              </span>
              <span className={styles.threadActions}>
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label={expanded ? 'Shrink window' : 'Expand window'}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button type="button" className={styles.iconBtn} aria-label="Close support" onClick={onClose}>
                  <ChromeIcon name="close" size={18} />
                </button>
              </span>
            </header>
            <div className={styles.threadBody}>
              {detail?.messages.map((m) => {
                const out = m.senderUserId === user?.id
                return (
                  <div key={m.id} className={styles.bubbleGroup}>
                    <div className={out ? styles.bubbleOut : styles.bubbleIn}>{m.body}</div>
                    <div className={out ? styles.bubbleMetaOut : styles.bubbleMetaIn}>
                      {out ? 'You' : 'Ten Four Support'} · {formatRelative(m.createdAt)}
                    </div>
                  </div>
                )
              })}
              {detail && detail.messages.length <= 1 && (
                <div className={styles.threadNotice}>
                  Thanks — the team has been notified and will reply right here.
                </div>
              )}
              <div ref={threadEndRef} />
            </div>
            <div className={styles.composerWrap}>
              <div className={styles.composer}>
                <textarea
                  className={styles.composerInput}
                  placeholder="Message…"
                  rows={1}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => composerKey(e, sendReply)}
                  aria-label="Reply"
                />
                <button
                  type="button"
                  className={styles.composerSend}
                  aria-label="Send reply"
                  disabled={addMessageMutation.isPending || replyText.trim().length === 0}
                  onClick={sendReply}
                >
                  <ArrowUp size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom tab bar (home/list only) ──────────────────────────── */}
        {(view.name === 'home' || view.name === 'list') && (
          <nav className={styles.tabBar} aria-label="Support sections">
            <button
              type="button"
              className={[styles.tab, view.name === 'home' && styles.tabActive]
                .filter(Boolean)
                .join(' ')}
              onClick={() => view.name !== 'home' && go({ name: 'home' }, 'back')}
            >
              <Home size={18} aria-hidden />
              <span>Home</span>
            </button>
            <button
              type="button"
              className={[styles.tab, view.name === 'list' && styles.tabActive]
                .filter(Boolean)
                .join(' ')}
              onClick={() => view.name !== 'list' && go({ name: 'list' }, 'forward')}
            >
              <span className={styles.tabIconWrap}>
                <MessageSquareText size={18} aria-hidden />
                {unread > 0 && (
                  <ClayCountBadge
                    count={unread}
                    label={`${unread} unread support messages`}
                    size="sm"
                    className={styles.tabBadge}
                  />
                )}
              </span>
              <span>Messages</span>
            </button>
          </nav>
        )}
        {/* Deep-link affordance kept explicit for a11y tools */}
        <span className={styles.srOnly} aria-hidden>
          <ChevronRight size={0} />
        </span>
      </div>
    </div>,
    document.body,
  )
}
